/* eslint-disable jsx-a11y/media-has-caption */
import { useTheme } from '@mui/material';
import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import WaveSurfer from 'wavesurfer.js';
import { Song } from 'services/store/songs/types';
import { useSharedWaveformStyles } from 'services/hooks/waveform';
import { renderFunction } from './helpers';

export interface WavesurferContextProps {
  isPlaying: boolean;
  isLoading: boolean;
  currentTime: number;
  song: Song | null;
  changeSong: (song: Song | null) => void;
  play: () => void;
  pause: () => void;
  togglePlayback: () => void;
  seekTo: (progress: number) => void;
  updateContainerRef: (container: HTMLElement) => void;
}

export const WavesurferContext = createContext<WavesurferContextProps>({
  isPlaying: false,
  isLoading: false,
  currentTime: 0,
  song: null,
  changeSong: () => {},
  play: () => {},
  pause: () => {},
  togglePlayback: () => {},
  seekTo: () => {},
  updateContainerRef: () => {},
});

const WavesurferProvider = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  const sharedWaveformStyles = useSharedWaveformStyles();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [song, setSong] = useState<Song | null>(null);
  const wavesurfer = useRef<WaveSurfer>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState<number>(0);

  const play = useCallback(() => wavesurfer.current?.play(), [wavesurfer]);

  const pause = useCallback(() => wavesurfer.current?.pause(), [wavesurfer]);

  const togglePlayback = useCallback(
    () => wavesurfer.current?.playPause(),
    [wavesurfer]
  );

  const seekTo = useCallback(
    (progress: number) => wavesurfer.current?.seekTo(progress),
    [wavesurfer]
  );

  const changeSong = useCallback(
    (newSong: Song | null) => {
      pause();
      setIsLoading(true);
      setSong(newSong);
    },
    [pause]
  );

  const createWavesurfer = useCallback(
    (container: HTMLElement) => {
      if (wavesurfer.current || !audioRef?.current) {
        return;
      }

      const wavesurferInstance = WaveSurfer.create({
        media: audioRef.current,
        container,
        // @ts-ignore
        splitChannels: false,
        mediaControls: false,
        renderFunction,
      });
      wavesurfer.current = wavesurferInstance;
    },
    [wavesurfer, audioRef]
  );

  const updateContainerRef = useCallback(
    (container: HTMLElement) => {
      if (wavesurfer.current) {
        wavesurfer.current.setOptions({ container });
        return;
      }

      createWavesurfer(container);
    },
    [wavesurfer, createWavesurfer]
  );

  useEffect(() => {
    if (!wavesurfer.current) {
      return;
    }

    wavesurfer.current.on('play', () => setIsPlaying(true));
    wavesurfer.current.on('pause', () => setIsPlaying(false));

    wavesurfer.current.on('timeupdate', (time) => {
      setCurrentTime(Math.floor(time));
    });

    wavesurfer.current.on('ready', () => {
      setIsLoading(false);
      play();
    });

    wavesurfer.current.on('decode', () => {
      if (song?.peaks) {
        return;
      }
      const peaks = wavesurfer.current?.exportPeaks({ channels: 1 });
      wavesurfer.current?.setOptions({ peaks });
    });
  }, [song, wavesurfer, play]);

  useEffect(() => {
    wavesurfer.current?.setOptions({
      waveColor: sharedWaveformStyles.waveColor,
      progressColor: sharedWaveformStyles.progressColor,
      barWidth: sharedWaveformStyles.barWidth,
      cursorWidth: 0,
      height: 'auto',
    });
  }, [wavesurfer, theme, sharedWaveformStyles]);

  useEffect(() => {
    if (!wavesurfer.current || !song) {
      return;
    }

    const { peaks } = song;
    const peaksData = peaks ? [peaks.data] : undefined;
    wavesurfer.current.load(song.musicFilePath, peaksData);
  }, [song, wavesurfer, pause]);

  return (
    <WavesurferContext.Provider
      value={{
        isPlaying,
        isLoading,
        currentTime,
        song,
        changeSong,
        play,
        pause,
        togglePlayback,
        seekTo,
        updateContainerRef,
      }}
    >
      <audio ref={audioRef} />
      {children}
    </WavesurferContext.Provider>
  );
};

export default WavesurferProvider;
